import gql from 'graphql-tag';
import { graphql } from '@apollo/client/react/hoc';
import { Errors } from '../../../Shared/Errors';
import { COURSE_QUERY } from '../CourseOverview/useSingleSeriesWithSegments';
import { withSeriesDayProgressQuery as SeriesDayProgressQuery } from './withSeriesDayProgress';
import {
  withRecordSeriesSegmentCompleted,
  withRecordSeriesSegmentCompletedVariables,
} from './__generated__/withRecordSeriesSegmentCompleted';

export const withRecordSeriesSegmentCompletedMutation = gql`
  mutation withRecordSeriesSegmentCompleted(
    $input: RecordSeriesSegmentCompletedInput!
  ) {
    recordSeriesSegmentCompleted(input: $input) {
      userSeriesInsights {
        id
      }
    }
  }
`;

export interface WithLogSeriesActivityResponsesChildProps {
  logSeriesActivity(
    variables: WithLogSeriesActivityVariables,
  ): Promise<WithLogSeriesActivityResponse>;
}

export interface WithLogSeriesActivityResponse {
  success: boolean;
  error?: Errors;
}

export interface WithLogSeriesActivityVariables {
  segmentId: string;
  dayId: string;
  seriesId: string;
  seconds: number;
  slug: string;
  clientSentAtUtcTimestamp: string;
  exploreCategoryId?: string | null;
}

/*
 * Ideally the recordSeriesSegmentCompleted mutation would return the
 * information we're requesting in SeriesDayProgressQuery and COURSE_QUERY,
 * from which we'd update the cache and avoid making a separate request.
 */
export const withLogSeriesActivity = graphql<
  Record<string, unknown>,
  withRecordSeriesSegmentCompleted,
  withRecordSeriesSegmentCompletedVariables,
  WithLogSeriesActivityResponsesChildProps
>(withRecordSeriesSegmentCompletedMutation, {
  props: ({ mutate }) => ({
    logSeriesActivity: async variables => {
      try {
        if (mutate) {
          const {
            dayId: sessionId,
            seconds: secondsToCompleteSegment,
            slug: _,
            ...remainingVariables
          } = { ...variables };
          const response = await mutate({
            refetchQueries: [
              {
                query: SeriesDayProgressQuery,
                variables: {
                  ...variables,
                  id: variables.dayId,
                },
              },
              {
                query: COURSE_QUERY,
                variables: {
                  slug: variables.slug,
                },
              },
            ],
            awaitRefetchQueries: true,
            variables: {
              input: {
                ...remainingVariables,
                sessionId,
                secondsToCompleteSegment,
              },
            },
          });

          if (response && response.data?.recordSeriesSegmentCompleted) {
            return {
              success: true,
            };
          }
        }
      } catch (e) {
        console.error(e); // to Sentry
      }

      return {
        success: false,
        error: Errors.ServerError,
      };
    },
  }),
});
